/**
 * Attachments — spec §5-F8, V3 R3.
 *
 * Drag-drop or choose, thumbnail grid, lightbox preview, download, delete.
 * Bytes go straight to storage via the presigned PUT; the API only ever sees
 * metadata.
 */

import { useState } from 'react';
import { Download, FileUp, ImageOff, Loader2, Paperclip, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ERROR_CODES } from '@virtuetasks/types';
import { ApiError, api } from '@/lib/api';
import {
  useAttachments,
  useDeleteAttachment,
  useUploadAttachment,
  type AttachmentEntity,
  type AttachmentView,
} from './api';

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const isImage = (mime: string): boolean => mime.startsWith('image/');

export function AttachmentSection({
  entityId,
  entityType = 'task',
  readOnly = false,
  title = 'Attachments',
}: {
  entityId: string;
  /** V4 F-1/F-2 — the same section serves projects and notes. */
  entityType?: AttachmentEntity;
  readOnly?: boolean;
  title?: string;
}) {
  const { data: attachments = [] } = useAttachments(entityType, entityId);
  const upload = useUploadAttachment(entityId);
  const remove = useDeleteAttachment(entityId);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<AttachmentView | null>(null);
  /**
   * Attachments whose image would not load — V10 §5(b).
   *
   * Held here rather than asked of the server per row: proving a byte range
   * exists costs a request per attachment, and the browser is already making
   * exactly that request when it tries to render the picture. A failed load
   * *is* the evidence.
   */
  const [unavailable, setUnavailable] = useState<ReadonlySet<string>>(() => new Set());

  async function add(files: FileList | File[]): Promise<void> {
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync({ file, entityType, entityId });
      } catch (err) {
        toast.error(
          err instanceof ApiError
            ? `${file.name}: ${err.message}`
            : `${file.name} could not be uploaded`,
        );
      }
    }
  }

  /**
   * Downloads use a short-lived presigned GET, resolved at click time (§F8).
   *
   * V10 §5 — **an anchor, not `window.open`.** The previous version `fetch`ed
   * the 302 endpoint, which followed the redirect and pulled the entire file
   * into memory only to discard it, then called `window.open` on the URL it
   * landed at. That call happens *after* an `await`, so the browser no longer
   * counts it as part of the click gesture and the popup blocker discards it:
   * the button did nothing at all, which is what the founder saw.
   *
   * The link is JSON now, and the anchor carries `download`. What actually
   * makes the file save under its own name is the `content-disposition` the
   * server signed into the URL — `download` is ignored cross-origin — but both
   * agree, so the local driver and a real bucket behave the same way.
   */
  async function download(attachment: AttachmentView): Promise<void> {
    try {
      const { data } = await api.get<{ url: string; fileName: string }>(
        `/attachments/${attachment._id}/download-link`,
      );
      const link = document.createElement('a');
      link.href = data.url;
      link.download = data.fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      // §5(b) — the row is here, the bytes are not. Say which.
      toast.error(
        err instanceof ApiError && err.code === ERROR_CODES.STORAGE_OBJECT_MISSING
          ? `${attachment.fileName} is unavailable — the file is not in this workspace’s storage.`
          : 'Could not get a download link',
      );
    }
  }

  return (
    <section data-testid="attachments">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="flex-1 text-[13px] font-medium">
          {title}{' '}
          {attachments.length > 0 && <span className="vt-muted">({attachments.length})</span>}
        </h3>
        {upload.isPending && <Loader2 className="size-3.5 animate-spin vt-muted" />}
      </div>

      {!readOnly && (
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void add(e.dataTransfer.files);
        }}
        className={`rounded-[6px] border border-dashed p-2 transition-colors duration-[120ms] ${
          dragging ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : 'border-[var(--border)]'
        }`}
        data-testid="attachment-dropzone"
      >
        <label className="flex cursor-pointer items-center gap-2 text-[12px] vt-muted">
          <FileUp className="size-4" />
          Drop files here, or choose
          <input
            type="file"
            multiple
            className="hidden"
            data-testid="attachment-input"
            onChange={(e) => {
              if (e.target.files) void add(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      )}

      {attachments.length > 0 && (
        <ul className="mt-2 grid gap-2 sm:grid-cols-2">
          {attachments.map((a) => (
            <li
              key={a._id}
              className="vt-surface flex items-center gap-2 p-2"
              data-testid="attachment-row"
            >
              {isImage(a.mimeType) && a.thumbUrl && !unavailable.has(a._id) ? (
                <button
                  type="button"
                  onClick={() => setPreview(a)}
                  aria-label={`Preview ${a.fileName}`}
                  className="size-10 shrink-0 overflow-hidden rounded-[4px]"
                >
                  <img
                    src={a.thumbUrl}
                    alt=""
                    data-testid="attachment-thumb"
                    className="size-full object-cover"
                    /**
                     * V10 §5(b) — an image whose bytes are in the old bucket
                     * cannot load, and a broken-image glyph is not an answer.
                     * The row says so in words instead.
                     */
                    onError={() => setUnavailable((ids) => new Set(ids).add(a._id))}
                  />
                </button>
              ) : (
                <span className="grid size-10 shrink-0 place-items-center rounded-[4px] bg-[var(--surface-2)]">
                  {unavailable.has(a._id) ? (
                    <ImageOff className="size-4 vt-muted" />
                  ) : (
                    <Paperclip className="size-4 vt-muted" />
                  )}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px]">{a.fileName}</div>
                <div className="text-[11px] vt-muted" data-testid="attachment-meta">
                  {unavailable.has(a._id) ? (
                    <span data-testid="attachment-unavailable">File unavailable</span>
                  ) : (
                    <>
                      {humanSize(a.sizeBytes)}
                      {a.uploadStatus !== 'complete' ? ` · ${a.uploadStatus}` : ''}
                    </>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Download ${a.fileName}`}
                onClick={() => void download(a)}
              >
                <Download />
              </Button>
              {!readOnly && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Delete ${a.fileName}`}
                onClick={async () => {
                  if (!window.confirm(`Delete ${a.fileName}?`)) return;
                  await remove.mutateAsync(a._id);
                }}
              >
                <Trash2 />
              </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {preview && (
        <div
          role="dialog"
          aria-label={`Preview of ${preview.fileName}`}
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-6"
          onClick={() => setPreview(null)}
        >
          <button
            type="button"
            aria-label="Close preview"
            className="absolute right-4 top-4 grid size-8 place-items-center rounded-full bg-white/10 text-white"
            onClick={() => setPreview(null)}
          >
            <X className="size-4" />
          </button>
          {unavailable.has(preview._id) ? (
            <p className="rounded-[8px] bg-[var(--surface)] px-4 py-3 text-[13px]" data-testid="preview-unavailable">
              File unavailable — {preview.fileName} is not in this workspace’s storage.
            </p>
          ) : (
            <img
              src={preview.previewUrl ?? preview.thumbUrl}
              alt={preview.fileName}
              className="max-h-full max-w-full rounded-[8px]"
              onError={() => setUnavailable((ids) => new Set(ids).add(preview._id))}
            />
          )}
        </div>
      )}
    </section>
  );
}
